import { createTranslator, type useTranslations } from "next-intl"
import english from "@/translations/en/quality-changes.json"

import { LEARNED_FOLDER_NAME } from "@/lib/ai/knowledge-base/folder-types"
import { KNOWLEDGE_BASE_HREF } from "@/lib/ai/knowledge-base/page-link"
import { formatShare } from "@/lib/analytics/format"
import type { ThreadTone } from "@/lib/conversation/thread-header"
import { formatClockTime, formatShortDate } from "@/lib/conversation/time-format"
import { utcToDateKeyTz } from "@/lib/date-utils"
import { diffLines, type DiffLine } from "@/lib/utils/text-diff"

import { MIN_SHARE_POPULATION, NO_VALUE, share } from "./metric-definitions"
import { TOPIC_CATEGORY_LABELS, questionWithKind } from "./outcome-display"
import type { TopicCategory } from "./outcomes"
import type { Learner } from "./self-learning-display"
import {
  DAY_MS,
  DRAFTING_DAYS,
  checkpointAt,
  firstRunAt,
  harmOf,
  nextCheckpoint,
  tightensRule,
  waitingClosesAt,
  type Harm,
} from "./suggestions/checkpoints"
import {
  COMPARED_VERDICTS,
  MAX_CHANGE_LINE_LENGTH,
  SUGGESTION_STATUS,
  type ChangeCounts,
  type ChangeStatus,
  type ArticleAuthor,
  type ChangeResult,
  type FindNowReport,
  type FindNowStatus,
  type Measurement,
  type PromptEditOperation,
  type PromptEditPayload,
  type SupersededReason,
  type SuggestionKind,
  type UndoneReason,
  type WaitingReason,
} from "./suggestions/schema"
export const CHANGE_STATES = ["checking", "working", "taking_back", "taken_back", "replaced"] as const
export type ChangeState = (typeof CHANGE_STATES)[number]

/** A change as its ledger row holds it. */
export interface StoredChange {
  kind: SuggestionKind;
  /** An instruction change's; `null` for an article. */
  operation: PromptEditOperation | null;
  /** Who wrote the section an article replaced; absent or `null` for an addition. */
  replacing?: ArticleAuthor | null;
  status: ChangeStatus;
  reason: string | null;
  appliedAutomatically: boolean;
  measureState: string | null;
  decidedAt: string;
  adoptedAt: string | null;
  result: ChangeResult | null;
}

export interface ChangeDescription {
  state: ChangeState;
  line: string;
}

export interface PooledShares {
  before: number;
  after: number;
}

type Rate = "resolved" | "left_without"

interface ComparedSides {
  before: ChangeCounts;
  after: ChangeCounts;
}

/** Inline applies the drafted change on the spot; review opens its dialog first. */
export type ApplyMode = "inline" | "review"

export interface WaitingDraft {
  kind: SuggestionKind;
  /** An instruction change's; `null` for an article. */
  operation: PromptEditOperation | null;
  reason: string | null;
  /** `null` when withheld from a caller who may not read the instructions, and for an article. */
  edit: PromptEditPayload | null;
  /** The change check's own sentence, where one ran and may be read. */
  checkReason: string | null;
  /** Who wrote the section an article replaces; absent or `null` for an addition. */
  replacing?: ArticleAuthor | null;
}

export interface WaitingDescription {
  /** What applying it changes. */
  headline: string;
  /** Under a limit's headline, what the change check found it loosens. */
  found: string | null;
  /** Why it waits for a person, where the headline does not say. */
  note: string | null;
  link: { href: string; label: string } | null;
  apply: ApplyMode;
  /** The person's no: keeping the line it would change, or not adding what it would add. */
  decline: string;
}

type WaitingAction =
  | "add_article"
  | "update_article"
  | "rewrite_article_yours"
  | "change_instructions"
  | "add_line"
  | "rewrite_line"
  | "remove_line"
  | "rewrite_yours"
  | "remove_yours"
  | "contradict_yours"

interface ArticleText {
  title: string;
  content: string;
}


const englishTranslator = createTranslator({locale: "en", messages: english, namespace: "QualityChanges"})

export const { CHANGE_STATE_LABELS, CHANGE_STATE_TONES, LEARNED_FROM_TEAM, learnedDocumentTitle, articleDestination, CHANGES_FIRST_PAGE, CHANGES_PAGE, describeChange, describeChangeMade, canUndoChange, refusalNote, undoneVersionLabel, pooledResolvedShares, learningTally, learnedThisWeek, latestChangeLine, describeWaiting, waitingCount, waitingListed, waitingSummary, draftMetaLine, closingDate, FIND_NOW_LABEL, FIND_NOW_NO_PERMISSION, findNowLine, findNowRefusal, findNowOutcome, waitingAsk, articleLines, changeLines } = getQualityChangeFormatters()

export function getQualityChangeFormatters(
  t: ReturnType<typeof useTranslations<"QualityChanges">> = englishTranslator,
  locale = "en",
  topicLabels: Record<TopicCategory, string> = TOPIC_CATEGORY_LABELS,
) {
  const CHANGE_STATE_LABELS: Record<ChangeState, string> = {
    checking: t("checking"),
    working: t("working"),
    taking_back: t("takingBack"),
    taken_back: t("takenBack"),
    replaced: t("replaced"),
  }

  const CHANGE_STATE_TONES: Record<ChangeState, ThreadTone> = {
    checking: "info",
    working: "success",
    taking_back: "warning",
    taken_back: "warning",
    replaced: "neutral",
  }

  const LEARNED_FROM_TEAM = t("learnedFromYourTeam")

  function learnedDocumentTitle(agentName: string, category: TopicCategory): string {
    return `${agentName} · ${topicLabels[category]}`
  }

  function articleDestination(
    agentName: string,
    category: TopicCategory,
    before: { title: string } | null
  ): string {
    return before
      ? t("replacesValue0InValue1", { value0: String(before.title), value1: String(LEARNED_FOLDER_NAME) })
      : t("addsToValue0InValue1", { value0: String(learnedDocumentTitle(agentName, category)), value1: String(LEARNED_FOLDER_NAME) })
  }

  const CHANGES_FIRST_PAGE = 10

  const CHANGES_PAGE = 100

  function describeChange(change: StoredChange, timezone: string, now: Date = new Date()): ChangeDescription {
    const measurement = change.result?.measurement ?? null

    switch (change.status) {
      case SUGGESTION_STATUS.APPLIED:
        return describeApplied(change, measurement, { timezone, now })
      case SUGGESTION_STATUS.UNDONE:
        return { state: "taken_back", line: undoneLine(change, measurement) }
      case SUGGESTION_STATUS.SUPERSEDED:
        return { state: "replaced", line: supersededLine(change.reason, change.kind) }
      default: {
        const exhaustive: never = change.status
        return exhaustive
      }
    }
  }

  function describeChangeMade(
    change: Pick<StoredChange, "kind" | "operation" | "replacing" | "appliedAutomatically">
  ): string {
    return change.appliedAutomatically ? changeAction(change) : t("value0YouApprovedThis", { value0: String(changeAction(change)) })
  }

  function canUndoChange(change: { status: string; adoptedAt: string | null }): boolean {
    return change.status === SUGGESTION_STATUS.APPLIED && change.adoptedAt === null
  }

  function refusalNote(kind: SuggestionKind, decision: "no" | "undo"): string {
    switch (kind) {
      case "prompt_edit":
        return decision === "no" ? t("theAgentWonTSuggestThisAgain") : t("theAgentWonTMakeItAgain")
      case "kb_addition":
        return t("theAgentWonTAddThisArticleAgain")
      default: {
        const exhaustive: never = kind
        return exhaustive
      }
    }
  }

  function undoneVersionLabel(reason: string | null): string {
    switch (reason as UndoneReason | null) {
      case "worse":
        return t("takenBackAutomatically")
      case "tool_gone":
        return t("takenBackToolTurnedOff")
      default:
        return t("changeUndone")
    }
  }

  function pooledResolvedShares(results: (ChangeResult | null)[]): PooledShares | null {
    const pooled = results.flatMap((result) => {
      const sides = poolableSides(result)
      return sides ? [sides] : []
    })
    const before = sumCounts(pooled.map((sides) => sides.before))
    const after = sumCounts(pooled.map((sides) => sides.after))
    if (before.requests < MIN_SHARE_POPULATION || after.requests < MIN_SHARE_POPULATION) return null
    return { before: before.resolvedByAgent / before.requests, after: after.resolvedByAgent / after.requests }
  }

  function poolableSides(result: ChangeResult | null): ComparedSides | null {
    const measurement = result?.measurement ?? null
    if (!measurement?.verdictAt || measurement.baseline?.widened || tightensRule(result)) return null
    return comparedSides(measurement)
  }

  function sumCounts(counts: ChangeCounts[]): Pick<ChangeCounts, "requests" | "resolvedByAgent"> {
    return counts.reduce(
      (sum, side) => ({ requests: sum.requests + side.requests, resolvedByAgent: sum.resolvedByAgent + side.resolvedByAgent }),
      { requests: 0, resolvedByAgent: 0 }
    )
  }

  function learningTally(
    learner: Learner,
    { improvements, takenBack, learnedQuestions }: { improvements: number; takenBack: number; learnedQuestions: PooledShares | null }
  ): string {
    return [
      t("improvements", {count: improvements}),
      t("value0TakenBack", { value0: String(takenBack.toLocaleString(locale)) }),
      ...(learnedQuestions
        ? [
            t("value0Value1Value2ResolvedByTheAgentAlone", { value0: String(QUESTIONS_LEARNED[learner]), value1: String(formatShare(learnedQuestions.before, NO_VALUE)), value2: String(formatShare(learnedQuestions.after, NO_VALUE)) }),
          ]
        : []),
    ].join(" · ")
  }

  function learnedThisWeek(learner: string | null, learned: number, takenBack: number): string | null {
    const who = learner ?? "Your agents"
    const things = t("things", {count: learned})
    const changes = t("changes", {count: takenBack})
    if (learned > 0 && takenBack > 0) return t("value0LearnedValue1AndTookBackValue2This", { value0: String(who), value1: String(things), value2: String(changes) })
    if (learned > 0) return t("value0LearnedValue1ThisWeek", { value0: String(who), value1: String(things) })
    if (takenBack > 0) return t("value0TookBackValue1ThisWeek", { value0: String(who), value1: String(changes) })
    return null
  }

  const QUESTIONS_LEARNED: Record<Learner, string> = {
    agent: t("questionsItLearned"),
    agents: t("questionsTheyLearned"),
  }

  function latestChangeLine(
    change: { title: string; topicLabel: string; topicCategory: TopicCategory; agentName: string },
    showAgent: boolean
  ): string {
    const latest = t("latestValue0Value1", { value0: String(questionWithKind(change.topicLabel, change.topicCategory, topicLabels)), value1: String(change.title) })
    return showAgent ? `${latest} · ${change.agentName}` : latest
  }

  function describeApplied(
    change: StoredChange,
    measurement: Measurement | null,
    view: { timezone: string; now: Date }
  ): ChangeDescription {
    if (change.adoptedAt) return { state: "replaced", line: t("youRewordedItItSPartOfYour") }

    const verdict = measurement?.verdict ?? null
    switch (verdict) {
      case null:
        return change.measureState === "final"
          ? { state: "working", line: t("keptItsResultWasnTMeasured") }
          : { state: "checking", line: checkingLine(change, measurement, view) }
      // Judged worse but still applied: its take-back was refused, and each weekly run tries it again.
      case "worse":
        return {
          state: "taking_back",
          line: t("answersGotWorseValue0ItCouldnTBe", { value0: String(harmLine(change.result, measurement)), value1: String(editedIn(change.kind)) }),
        }
      case "helped":
        return { state: "working", line: t("resolvedByTheAgentAloneValue0", { value0: String(shareChange(measurement, "resolved")) }) }
      case "no_change":
        return { state: "working", line: noChangeLine(change.result, measurement) }
      case "not_enough":
        return { state: "working", line: t("keptTooFewConversationsToTell") }
      case "no_baseline":
        return { state: "working", line: t("keptNothingEarlierToCompare") }
      default: {
        const exhaustive: never = verdict
        return exhaustive
      }
    }
  }

  function editedIn(kind: SuggestionKind): string {
    return kind === "kb_addition" ? t("theKnowledgeBase") : t("theSystemPrompt")
  }

  function checkingLine(
    change: StoredChange,
    measurement: Measurement | null,
    { timezone, now }: { timezone: string; now: Date }
  ): string {
    if (measurement?.learnedWhileSilent && measurement.clockStart === null) {
      return t("checkingStartsWhenTheAgentAnswersThisQuestion")
    }
    const upcoming = nextCheckpoint(measurement?.checks.map((check) => check.checkpoint) ?? [])
    const clockStart = new Date(measurement?.clockStart ?? change.decidedAt)
    const due = checkpointAt(clockStart, upcoming === "extension" ? "extension" : "final")
    const until = firstRunAt(due > now ? due : now)
    return t("checkingUntilValue0", { value0: String(formatShortDate(until.toISOString(), timezone, locale)) })
  }

  function noChangeLine(result: ChangeResult | null, measurement: Measurement | null): string {
    if (tightensRule(result)) return t("noClearChangeCustomersLeftWithoutAnAnswer", { value0: String(shareChange(measurement, "left_without")) })
    const resolved = t("resolvedByTheAgentAloneValue02", { value0: String(shareChange(measurement, "resolved")) })
    return measurement?.baseline?.widened
      ? t("keptValue0WithTooLittleBeforeItTo", { value0: String(resolved) })
      : t("noClearChangeValue0", { value0: String(resolved) })
  }

  function undoneLine(change: StoredChange, measurement: Measurement | null): string {
    switch (change.reason as UndoneReason | null) {
      case "worse":
        return t("takenBackAutomaticallyValue0", { value0: String(harmLine(change.result, measurement)) })
      case "by_you":
        return t("undoneByYou")
      case "tool_gone":
        return t("takenBackAutomaticallyAToolItReliesOn")
      default:
        return t("undone")
    }
  }

  function supersededLine(reason: string | null, kind: SuggestionKind): string {
    const article = kind === "kb_addition"
    switch (reason as SupersededReason | null) {
      case "rewritten":
        return article ? t("replacedByANewerArticleOnTheSame") : t("replacedByANewerChangeToTheSame")
      case "role_applied":
        return t("replacedWhenARoleWasAppliedToThe")
      case "edited":
        return article ? t("removedWithOtherFactsInTheKnowledgeBase") : t("replacedByYourEditsToTheInstructions")
      default:
        return t("replaced")
    }
  }

  function harmLine(result: ChangeResult | null, measurement: Measurement | null): string {
    const judged = measurement?.checks.findLast((check) => check.verdict === "worse")
    const baseline = measurement?.baseline
    if (!judged || !baseline) return t("answersGotWorse")

    const harm: Harm = harmOf(judged.checkpoint, baseline, judged.after, tightensRule(result)) ?? "left_without"
    const change = `${formatShare(rate(baseline, harm), NO_VALUE)} → ${formatShare(rate(judged.after, harm), NO_VALUE)}`
    switch (harm) {
      case "left_without":
        return t("moreCustomersLeftWithoutAnAnswerValue0", { value0: String(change) })
      case "fewer_resolved":
        return t("fewerResolvedByTheAgentAloneValue0", { value0: String(change) })
      default: {
        const exhaustive: never = harm
        return exhaustive
      }
    }
  }

  function shareChange(measurement: Measurement | null, metric: Rate): string {
    const sides = comparedSides(measurement)
    if (!sides) return NO_VALUE
    return `${formatShare(rate(sides.before, metric), NO_VALUE)} → ${formatShare(rate(sides.after, metric), NO_VALUE)}`
  }

  function rate(counts: ChangeCounts, metric: Rate | Harm): number | null {
    return share(metric === "left_without" ? counts.leftWithout : counts.resolvedByAgent, counts.requests)
  }

  function comparedSides(measurement: Measurement | null): ComparedSides | null {
    const after = measurement?.checks.at(-1)?.after
    if (!measurement?.verdict || !COMPARED_VERDICTS.includes(measurement.verdict)) return null
    return measurement.baseline && after ? { before: measurement.baseline, after } : null
  }

  function changeAction({ kind, operation, replacing }: Pick<StoredChange, "kind" | "operation" | "replacing">): string {
    switch (kind) {
      case "kb_addition":
        return replacing ? t("updatedAnArticle") : t("addedAnArticle")
      case "prompt_edit":
        return operation === null ? t("changedTheInstructions") : LINE_ACTIONS[operation]
      default: {
        const exhaustive: never = kind
        return exhaustive
      }
    }
  }

  const LINE_ACTIONS: Record<PromptEditOperation, string> = {
    append: t("addedALine"),
    replace: t("rewroteALine"),
    remove: t("removedALine"),
  }

  function describeWaiting(draft: WaitingDraft): WaitingDescription {
    const reason = draft.reason as WaitingReason | null
    return {
      headline: WAITING_ACTIONS[waitingAction(draft)].headline,
      found: reason === "limit" ? draft.checkReason : null,
      note: waitingNote(reason, draft.kind),
      link: reason === "kb_limit" || reason === "kb_off" ? { href: KNOWLEDGE_BASE_HREF, label: t("openTheKnowledgeBase") } : null,
      apply: applyMode(draft, reason),
      decline: declineLabel(draft),
    }
  }

  function declineLabel({ operation, replacing }: Pick<WaitingDraft, "operation" | "replacing">): string {
    if (replacing) return t("keepTheCurrentArticle")
    return operation === "replace" || operation === "remove" ? t("keepMine") : t("donTAdd")
  }

  function waitingCount(waiting: number): string {
    return t("waitingCount", {count: waiting})
  }

  function waitingListed(listed: number): string {
    return t("showingTheValue0NewestDecideOnTheseTo", { value0: String(listed.toLocaleString(locale)) })
  }

  function waitingSummary(waiting: number): string {
    return t("waitingSummary", {count: waiting})
  }

  function draftedDays(draftedFrom: string, timezone: string): string {
    const from = Date.parse(draftedFrom)
    if (new Date(from).getUTCDay() === 1 && from % DAY_MS === 0) return t("theWeekOfValue0", { value0: String(formatShortDate(draftedFrom, "UTC", locale)) })
    const pressed = new Date(from + DRAFTING_DAYS * DAY_MS).toISOString()
    return t("inTheSevenDaysToValue0", { value0: String(formatShortDate(pressed, timezone, locale)) })
  }

  function draftMetaLine(
    draft: { sessions: number; draftedFrom: string; createdAt: string },
    timezone: string
  ): string {
    return [
      ...(draft.sessions > 0 ? [conversationsNotResolved(draft.sessions)] : []),
      draftedDays(draft.draftedFrom, timezone),
      t("closesValue0", { value0: String(closingDate(draft.createdAt, timezone)) }),
    ].join(" · ")
  }

  function conversationsNotResolved(sessions: number): string {
    return t("unresolvedConversations", {count: sessions})
  }

  function closingDate(createdAt: string, timezone: string): string {
    return formatShortDate(waitingClosesAt(new Date(createdAt)).toISOString(), timezone, locale)
  }

  const FIND_NOW_LABEL = t("findImprovementsNow")

  const FIND_NOW_NO_PERMISSION = t("onlyPeopleWhoCanManageAgentsCanLook")

  function findNowLine(status: FindNowStatus, timezone: string, now: Date = new Date()): string {
    switch (status.state) {
      case "available":
        return t("looksAtTheConversationsOfTheLastSeven")
      case "running":
        return t("lookingAtRecentConversations")
      case "available_later":
        return availableAgain(status.availableAt, timezone, now)
      case "nothing_new":
        return t("noNewlyReviewedConversationsSinceTheLastRun")
      case "not_drafted":
        return t("disabledAgentsAndAgentsWithSelfLearningOff")
      default: {
        const exhaustive: never = status
        return exhaustive
      }
    }
  }

  function availableAgain(at: string, timezone: string, now: Date): string {
    const clock = formatClockTime(at, timezone, locale)
    return utcToDateKeyTz(at, timezone) === utcToDateKeyTz(now.toISOString(), timezone)
      ? t("availableAgainAtValue0", { value0: String(clock) })
      : t("availableAgainValue0AtValue1", { value0: String(formatShortDate(at, timezone, locale)), value1: String(clock) })
  }

  function findNowRefusal(status: FindNowStatus, timezone: string, now: Date = new Date()): string {
    return status.state === "running"
      ? t("alreadyLookingAtRecentConversationsTryAgainWhen")
      : findNowLine(status, timezone, now)
  }

  function findNowOutcome(
    lastRun: NonNullable<FindNowReport["lastRun"]>
  ): { tone: "success" | "info" | "error"; message: string } {
    if (lastRun.found > 0) {
      // Found drafts wait for a person or were applied by the agent itself, as its mode allows.
      const found = t("newImprovements", {count: lastRun.found})
      return { tone: "success", message: t("foundValue0", { value0: String(found) }) }
    }
    if (!lastRun.complete) {
      return { tone: "error", message: t("lookingAtRecentConversationsStoppedEarlyTryAgain") }
    }
    switch (lastRun.held) {
      case "pace":
        return { tone: "info", message: t("nothingNewSelfLearningHasMadeAsMany") }
      case "allowance":
        return { tone: "info", message: t("nothingNewThisWeekSAllowanceForFinding") }
      case null:
        return { tone: "info", message: t("nothingNewToSuggestFromTheLastSeven") }
      default: {
        const exhaustive: never = lastRun.held
        return exhaustive
      }
    }
  }

  function waitingAsk(draft: Pick<WaitingDraft, "kind" | "operation" | "reason" | "replacing">): string {
    return WAITING_ACTIONS[waitingAction(draft)].ask
  }

  const WAITING_ACTIONS: Record<WaitingAction, { headline: string; ask: string }> = {
    add_article: {
      headline: t("thisAddsAnArticleToTheKnowledgeBase"),
      ask: t("wantsToAddAnArticleToTheKnowledge"),
    },
    update_article: {
      headline: t("thisUpdatesAnArticleInTheKnowledgeBase"),
      ask: t("wantsToUpdateAnArticleInTheKnowledge"),
    },
    rewrite_article_yours: { headline: t("thisRewritesAnArticleYouWrote"), ask: t("wantsToRewriteAnArticleYouWrote") },
    change_instructions: { headline: t("thisChangesTheAgentSInstructions"), ask: t("wantsToChangeItsInstructions") },
    add_line: {
      headline: t("thisAddsALineToTheAgentS"),
      ask: t("wantsToAddALineToItsInstructions"),
    },
    rewrite_line: {
      headline: t("thisRewritesALineOfTheAgentS"),
      ask: t("wantsToRewriteALineOfItsInstructions"),
    },
    remove_line: {
      headline: t("thisRemovesALineFromTheAgentS"),
      ask: t("wantsToRemoveALineFromItsInstructions"),
    },
    rewrite_yours: { headline: t("thisRewritesALineYouWrote"), ask: t("wantsToRewriteALineYouWrote") },
    remove_yours: { headline: t("thisRemovesALineYouWrote"), ask: t("wantsToRemoveALineYouWrote") },
    contradict_yours: {
      headline: t("thisAddsALineThatContradictsOneYou"),
      ask: t("wantsToAddALineThatContradictsOne"),
    },
  }

  function waitingAction({
    kind,
    operation,
    reason,
    replacing,
  }: Pick<WaitingDraft, "kind" | "operation" | "reason" | "replacing">): WaitingAction {
    switch (kind) {
      case "kb_addition":
        return articleAction(replacing ?? null)
      case "prompt_edit":
        return reason === "your_text" ? yourTextAction(operation) : instructionAction(operation)
      default: {
        const exhaustive: never = kind
        return exhaustive
      }
    }
  }

  function articleAction(replacing: ArticleAuthor | null): WaitingAction {
    switch (replacing) {
      case null:
        return "add_article"
      case "learned":
        return "update_article"
      case "yours":
        return "rewrite_article_yours"
      default: {
        const exhaustive: never = replacing
        return exhaustive
      }
    }
  }

  function instructionAction(operation: PromptEditOperation | null): WaitingAction {
    switch (operation) {
      case "append":
        return "add_line"
      case "replace":
        return "rewrite_line"
      case "remove":
        return "remove_line"
      case null:
        return "change_instructions"
      default: {
        const exhaustive: never = operation
        return exhaustive
      }
    }
  }

  function yourTextAction(operation: PromptEditOperation | null): WaitingAction {
    switch (operation) {
      case "replace":
        return "rewrite_yours"
      case "remove":
        return "remove_yours"
      case "append":
      case null:
        return "contradict_yours"
      default: {
        const exhaustive: never = operation
        return exhaustive
      }
    }
  }

  function waitingNote(reason: WaitingReason | null, kind: SuggestionKind): string | null {
    switch (reason) {
      case "limit":
        return t("itTouchesARuleLikeDiscountsRefundsHandover")
      case "kb_article":
        return t("newArticlesWaitForYourOKBeforeThey")
      case "kb_limit":
        return t("yourKnowledgeBaseIsFullRemoveAnArticle")
      case "kb_off":
        return t("theDocumentItBelongsInIsTurnedOff")
      case "after_rejection":
        return t("youSaidNoToAnEarlierChangeFor")
      case "after_edit":
        return t("youEditedOutAnEarlierChangeForThis")
      case "inherited":
        return kind === "kb_addition"
          ? t("thisAgentUsesYourSharedInstructionsSoIt")
          : t("thisAgentUsesYourSharedInstructionsApplyingGives")
      default:
        return null
    }
  }

  function applyMode(draft: WaitingDraft, reason: WaitingReason | null): ApplyMode {
    if (draft.kind === "kb_addition" || reason === "inherited") return "review"
    return draft.edit && isLongEdit(draft.edit) ? "review" : "inline"
  }

  function isLongEdit(edit: PromptEditPayload): boolean {
    return changeLines(edit).some(({ text }) => text.includes("\n") || text.length > MAX_CHANGE_LINE_LENGTH)
  }

  function articleLines(before: ArticleText, after: ArticleText): DiffLine[] {
    return diffLines(`${before.title}\n${before.content}`, `${after.title}\n${after.content}`)
  }

  function changeLines(edit: PromptEditPayload): DiffLine[] {
    switch (edit.operation) {
      case "append":
        return [{ type: "add", text: edit.instruction }]
      case "replace":
        return [
          { type: "remove", text: edit.target },
          { type: "add", text: edit.instruction },
        ]
      case "remove":
        return [{ type: "remove", text: edit.target }]
      default: {
        const exhaustive: never = edit
        return exhaustive
      }
    }
  }

  return { CHANGE_STATE_LABELS, CHANGE_STATE_TONES, LEARNED_FROM_TEAM, learnedDocumentTitle, articleDestination, CHANGES_FIRST_PAGE, CHANGES_PAGE, describeChange, describeChangeMade, canUndoChange, refusalNote, undoneVersionLabel, pooledResolvedShares, learningTally, learnedThisWeek, latestChangeLine, describeWaiting, waitingCount, waitingListed, waitingSummary, draftMetaLine, closingDate, FIND_NOW_LABEL, FIND_NOW_NO_PERMISSION, findNowLine, findNowRefusal, findNowOutcome, waitingAsk, articleLines, changeLines }
}
